import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

interface Column {
    field: string;
    title: string;
    sorting?: boolean;
    align?: string;
    render?: (row: any) => any;
}

interface QuickCopyDropdownProps {
    columns: Column[];
    tableName: string;
    selectedRows?: any[];
    trackBy?: string;
    entities?: any[];
    defaultColumnFields?: string[];
}

const QuickCopyDropdown: React.FC<QuickCopyDropdownProps> = ({
    columns,
    tableName,
    selectedRows = [],
    trackBy = 'id',
    entities = [],
    defaultColumnFields = [],
}) => {
    // State to track selected column
    const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
    
    // State to track copying status
    const [isCopying, setIsCopying] = useState<boolean>(false);
    const [isCopied, setIsCopied] = useState<boolean>(false);

    // Filter out action columns and columns without field property
    const copyableColumns = columns.filter((column) => {
        // Exclude action columns
        if (column.field === 'action') {
            return false;
        }
        
        // Only include columns that have a field property
        if (!column.field) {
            return false;
        }
        
        return true;
    });

    // Convert columns to dropdown options format
    const columnOptions = copyableColumns.map((column) => ({
        label: column.title,
        value: column.field,
    }));

    useEffect(() => {
        const hasCurrentSelection = !!selectedColumn && copyableColumns.some((column) => column.field === selectedColumn);

        if (hasCurrentSelection) {
            return;
        }

        for (const field of defaultColumnFields) {
            if (copyableColumns.some((column) => column.field === field)) {
                setSelectedColumn(field);
                return;
            }
        }

        if (copyableColumns.some((column) => column.field === 'vin_number')) {
            setSelectedColumn('vin_number');
            return;
        }

        if (copyableColumns.some((column) => column.field === 'vin')) {
            setSelectedColumn('vin');
            return;
        }

        setSelectedColumn(null);
    }, [columns, selectedColumn, defaultColumnFields]);

    // Handle the copy button click
    const handleQuickCopy = async () => {
        // Check if a column is selected
        if (!selectedColumn) {
            return;
        }

        // Set copying state to true
        setIsCopying(true);
        setIsCopied(false);

        try {
            // Find the table element
            // PrimeReact DataTable creates a table with class 'p-datatable-table'
            // We need to find it within the component that has our table name
            const tableElement = document.querySelector(`[data-table-name="${tableName}"] .p-datatable-table`) as HTMLTableElement;
            
            // If table not found by data attribute, try to find it by looking for the table in the page
            // This is a fallback approach
            let table: HTMLTableElement | null = tableElement;
            
            if (!table) {
                // Try to find table by looking for PrimeReact DataTable
                const allTables = document.querySelectorAll('.p-datatable-table');
                if (allTables.length > 0) {
                    // Use the first table found (assuming it's our table)
                    table = allTables[0] as HTMLTableElement;
                }
            }

            if (!table) {
                console.error('Table not found');
                setIsCopying(false);
                return;
            }

            // Find all table body rows (skip header row)
            const tbody = table.querySelector('tbody');
            if (!tbody) {
                console.error('Table body not found');
                setIsCopying(false);
                return;
            }

            const rows = Array.from(tbody.querySelectorAll('tr'));
            
            // Find the header row to get column order
            const thead = table.querySelector('thead');
            if (!thead) {
                console.error('Table header not found');
                setIsCopying(false);
                return;
            }

            // Get all header cells to determine column order
            const headerRow = thead.querySelector('tr');
            if (!headerRow) {
                console.error('Header row not found');
                setIsCopying(false);
                return;
            }

            const headerCells = Array.from(headerRow.querySelectorAll('th'));
            
            // Find the index of our selected column in the visible columns
            // We need to account for selection columns and expander columns that might be present
            let columnIndex = -1;
            let visibleColumnIndex = 0;
            
            // Loop through header cells to find our column
            headerCells.forEach((headerCell, index) => {
                // Skip selection and expander columns (they have specific classes)
                const isSelectionColumn = headerCell.classList.contains('p-selection-column');
                const isExpanderColumn = headerCell.querySelector('.p-row-toggler') !== null;
                
                if (!isSelectionColumn && !isExpanderColumn) {
                    // Check if this header cell matches our selected column
                    // Trim whitespace from both texts for comparison
                    const headerText = headerCell.textContent?.trim() || '';
                    const selectedColumnTitle = copyableColumns.find(col => col.field === selectedColumn)?.title?.trim() || '';
                    
                    if (headerText === selectedColumnTitle) {
                        columnIndex = index;
                    }
                    visibleColumnIndex++;
                }
            });
            
            // Array to store all values from the selected column
            const columnValues: string[] = [];

            // Check if we should filter by selected rows
            const hasSelectedRows = selectedRows && selectedRows.length > 0;
            const hasEntities = entities && entities.length > 0;
            let selectedRowIds: Set<string | number> = new Set();
            
            if (hasSelectedRows) {
                // Extract trackBy values from selected rows (e.g., IDs)
                selectedRows.forEach((selectedRow: any) => {
                    const rowId = selectedRow[trackBy];
                    if (rowId !== undefined && rowId !== null) {
                        selectedRowIds.add(rowId);
                    }
                });
            }

            // If we found the column index, extract values
            if (columnIndex >= 0) {
                // Track the entity index (to skip spacer rows in DOM)
                let entityIndex = 0;

                // Loop through each data row in the DOM
                rows.forEach((row, domRowIndex) => {
                    // Skip spacer rows - check if row has meaningful content
                    // Spacer rows for virtual scroll typically don't have selectable content
                    const rowCheckbox = row.querySelector('td.p-selection-column input[type="checkbox"]');
                    
                    // If we have selected rows, check if this row should be included
                    if (hasSelectedRows) {
                        let shouldIncludeRow = false;
                        
                        // Method 1: If we have entities, match by entity index and check if entity is selected
                        if (hasEntities && entityIndex < entities.length) {
                            const rowEntity = entities[entityIndex];
                            
                            // Skip spacer entities
                            if (rowEntity && (rowEntity[trackBy] === '__spacer__' || rowEntity.__isSpacer)) {
                                return; // Skip spacer entity
                            }
                            
                            if (rowEntity) {
                                const rowId = rowEntity[trackBy];
                                shouldIncludeRow = selectedRowIds.has(rowId);
                            }
                            entityIndex++; // Increment for next iteration
                        } else {
                            // Method 2: Fallback - check checkbox state directly from DOM
                            if (rowCheckbox) {
                                shouldIncludeRow = (rowCheckbox as HTMLInputElement).checked;
                            } else {
                                // No checkbox but has selectedRows - this shouldn't happen, but if it does, skip
                                return;
                            }
                        }
                        
                        // Skip this row if not selected
                        if (!shouldIncludeRow) {
                            return;
                        }
                    } else {
                        // No selected rows filter - process all rows, but skip spacer rows
                        if (hasEntities && entityIndex < entities.length) {
                            const rowEntity = entities[entityIndex];
                            // Skip spacer rows
                            if (rowEntity && (rowEntity[trackBy] === '__spacer__' || rowEntity.__isSpacer)) {
                                return; // Skip spacer, don't increment
                            }
                            entityIndex++;
                        }
                    }
                    
                    // Find all cells in this row
                    const cells = Array.from(row.querySelectorAll('td'));
                    
                    // Get the cell at our column index
                    if (cells[columnIndex]) {
                        const cell = cells[columnIndex];
                        
                        // Check if this is a VIN column (vin_number or vin)
                        const isVinColumn = selectedColumn === 'vin_number' || selectedColumn === 'vin';
                        
                        if (isVinColumn) {
                            // For VIN columns, extract all VINs and put each on a new line
                            // First, check if there are child elements (like divs containing VinWithType components)
                            const childElements = Array.from(cell.children);
                            
                            if (childElements.length > 0) {
                                // Extract VINs from each child element separately
                                // This handles cases where VINs are in separate divs (like in AlpInboundLoadList)
                                // Each div typically contains one VinWithType component with one VIN
                                childElements.forEach((child) => {
                                    // Get text content from the child element (this will get VIN from Button inside VinWithType)
                                    const childText = child.textContent?.trim() || '';
                                    
                                    if (childText) {
                                        // For VIN columns, each child div typically contains exactly one VIN
                                        // But we'll split by separators in case a child contains multiple VINs
                                        const vins = childText.split(/[\s,\n\r\t]+/).filter((vin: string) => {
                                            const trimmed = vin.trim();
                                            // Only include non-empty strings (VINs are typically 17 characters)
                                            // Filter out very short strings that might be artifacts
                                            return trimmed.length >= 10; // VINs are at least 10 characters
                                        });
                                        
                                        // Add each VIN from this child as a separate entry
                                        // This ensures each VIN is on its own line when joined with \n
                                        vins.forEach((vin: string) => {
                                            const trimmedVin = vin.trim();
                                            // Only add if it's a valid VIN (length check) and not already added
                                            if (trimmedVin && trimmedVin.length >= 10 && !columnValues.includes(trimmedVin)) {
                                                columnValues.push(trimmedVin);
                                            }
                                        });
                                        
                                        // If no VINs were found after splitting, but childText exists,
                                        // it might be a single VIN without separators - add it directly
                                        if (vins.length === 0 && childText.length >= 10) {
                                            if (!columnValues.includes(childText)) {
                                                columnValues.push(childText);
                                            }
                                        }
                                    }
                                });
                                
                                // If no VINs were found in child elements, fall back to cell text
                                // This handles cases where VINs might be directly in the cell without child divs
                                if (columnValues.length === 0) {
                                    const cellText = cell.textContent || '';
                                    if (cellText.trim()) {
                                        // Split by whitespace and commas
                                        const vins = cellText.split(/[\s,\n\r\t]+/).filter((vin: string) => {
                                            const trimmed = vin.trim();
                                            return trimmed.length > 0;
                                        });
                                        vins.forEach((vin: string) => {
                                            const trimmedVin = vin.trim();
                                            if (trimmedVin) {
                                                columnValues.push(trimmedVin);
                                            }
                                        });
                                    }
                                }
                            } else {
                                // No child elements, get text directly from cell
                                // This handles simple cases where VINs are just text in the cell
                                const cellText = cell.textContent || '';
                                if (cellText.trim()) {
                                    // Split by multiple separators: spaces, commas, newlines, tabs, and any whitespace
                                    const vins = cellText.split(/[\s,\n\r\t]+/).filter((vin: string) => {
                                        const trimmed = vin.trim();
                                        // Only include non-empty strings
                                        return trimmed.length > 0;
                                    });
                                    
                                    // Add each VIN as a separate entry (each will be on a new line when joined)
                                    vins.forEach((vin: string) => {
                                        const trimmedVin = vin.trim();
                                        if (trimmedVin) {
                                            columnValues.push(trimmedVin);
                                        }
                                    });
                                }
                            }
                        } else {
                            // For other columns, check if the cell contains multiple text elements
                            // Look for direct child elements that might contain separate values
                            const childElements = Array.from(cell.children);
                            
                            // Get the text content from the cell
                            let cellText = cell.textContent || '';
                            
                            if (childElements.length > 0) {
                                // If cell has child elements, extract text from each child separately
                                const childTexts: string[] = [];
                                
                                childElements.forEach((child) => {
                                    const childText = child.textContent?.trim() || '';
                                    if (childText) {
                                        // Clean up whitespace within each child
                                        const cleanedText = childText.replace(/\s+/g, ' ').trim();
                                        if (cleanedText) {
                                            childTexts.push(cleanedText);
                                        }
                                    }
                                });
                                
                                // If we found child texts, add each one separately
                                if (childTexts.length > 0) {
                                    columnValues.push(...childTexts);
                                } else {
                                    // Fallback: if child elements exist but no text, use cell text
                                    cellText = cellText.replace(/\s+/g, ' ').trim();
                                    if (cellText) {
                                        columnValues.push(cellText);
                                    }
                                }
                            } else {
                                // No child elements, just get the text content directly
                                cellText = cellText.replace(/\s+/g, ' ').trim();
                                
                                // Only add non-empty values
                                if (cellText) {
                                    columnValues.push(cellText);
                                }
                            }
                        }
                    }
                });
            } else {
                console.warn('Column not found in table');
                setIsCopying(false);
                return;
            }

            // If we found values, copy them to clipboard
            if (columnValues.length > 0) {
                // Join all values with newline character
                const textToCopy = columnValues.join('\n');
                
                // Copy to clipboard using Clipboard API
                await navigator.clipboard.writeText(textToCopy);
                
                // Show "Copying..." for 1.5 seconds
                setTimeout(() => {
                    setIsCopying(false);
                    setIsCopied(true);
                    
                    // Show "Copied!" for 2 seconds, then reset
                    setTimeout(() => {
                        setIsCopied(false);
                    }, 2000);
                }, 1500);
            } else {
                // No values found
                setIsCopying(false);
                console.warn('No values found for selected column');
            }
        } catch (error) {
            // Handle any errors
            console.error('Failed to copy:', error);
            setIsCopying(false);
            setIsCopied(false);
        }
    };

    // Get the tooltip text based on current state
    const getTooltipText = () => {
        if (isCopying) {
            return 'Copying...';
        } else if (isCopied) {
            return 'Copied!';
        }
        return 'Copy column to clipboard';
    };

    // Get button label
    const getButtonLabel = () => {
        if (isCopying) {
            return 'Copying...';
        } else if (isCopied) {
            return 'Copied!';
        }
        return '';
    };

    // Don't render if there are no copyable columns
    if (columnOptions.length === 0) {
        return null;
    }

    return (
        <div className="d-flex align-items-center gap-2">
            <Dropdown
                value={selectedColumn}
                options={columnOptions}
                onChange={(e) => setSelectedColumn(e.value)}
                placeholder="Select column to copy"
                className="p-inputtext-sm"
                style={{ minWidth: '200px' }}
            />
            <Button
                icon="pi pi-copy"
                size="small"
                outlined
                onClick={handleQuickCopy}
                disabled={!selectedColumn || isCopying}
                tooltip={getTooltipText()}
                tooltipOptions={{ position: 'top' }}
            >
                {getButtonLabel()}
            </Button>
        </div>
    );
};

export default QuickCopyDropdown;

